import { featureFlagsAtom } from '@polaris/core/stores'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  ChevronRight,
  CircleUser,
  Gift,
  MessageCircleQuestion,
  Plus,
  Settings,
  ThumbsUp,
} from 'lucide-react'
import { motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { useAccountsActions } from '@/src/stores/accounts/actions'
import { preferredAvatarAtom } from '@/src/stores/accounts/atoms'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import { loginDialogOpenAtom } from '@/src/stores/onboarding'
import {
  usePortfolioDrawer,
  usePortfolioView,
} from '@/src/stores/portfolio-drawer/actions'
import {
  openSelectWalletsDialog,
  selectWalletsDialogAtom,
} from '@/src/stores/select-wallets'
import { useLogout } from '@/src/stores/user/actions'
import { userAtom } from '@/src/stores/user/atoms'
import { walletOptionsAtom } from '@/src/stores/wallets/atoms'
import { cn } from '@/src/utils/styles'
import { AvatarImage, DEFAULT_AVATAR } from '../../accounts/avatar'
import { showIntercomWidget } from '../../intercom'
import { Button } from '../../ui/button'
import {
  ListRowButton,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../../ui/list-row'
import { showWalletsDisconnectedToast } from '../../ui/toast'
import { WalletStack } from '../../ui/wallet-stack'

export function Account() {
  const { close } = usePortfolioDrawer()
  const { updateView, reset } = usePortfolioView()
  const featureFlags = useAtomValue(featureFlagsAtom)
  const { groupedAccounts } = useAtomValue(walletAccountsAtom)
  const { aggregatedWallets, disconnectAll } = useAtomValue(walletOptionsAtom)
  const avatar = useAtomValue(preferredAvatarAtom)
  const user = useAtomValue(userAtom)
  const { ampli } = useAmplitudeAnalytics()
  const setDialogState = useSetAtom(selectWalletsDialogAtom)
  const setLoginDialogOpen = useSetAtom(loginDialogOpenAtom)
  const { removeAccounts } = useAccountsActions()

  const connectedWallets = aggregatedWallets.filter(
    (wallet) => Object.values(wallet.disconnectors).length > 0,
  )

  const logout = useLogout()

  if (!user) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col gap-4 pb-page-gutter'
    >
      <div className='flex flex-col items-center gap-3 pb-4'>
        {user.id === 'guest' ? (
          <AvatarImage
            layoutId='portfolio-avatar'
            avatar='cloud-man'
            size={80}
            className='rounded-full'
          />
        ) : (
          <AvatarImage
            layoutId='portfolio-avatar'
            avatar={avatar !== 'not-set' ? avatar : DEFAULT_AVATAR}
            size={80}
            className='rounded-full cursor-pointer'
            onClick={() => updateView({ view: 'manage-account' })}
          />
        )}
        <div className='flex flex-col items-center text-center'>
          <h3 className='text-primary text-xl font-semibold'>{user.name}</h3>
          {user.id === 'guest' ? (
            featureFlags.newLoginFlow ? (
              <Button
                type='button'
                size='sm'
                variant='primary'
                className='mt-3'
                onClick={() => setLoginDialogOpen(true)}
              >
                Sign up
              </Button>
            ) : (
              <Link href='/login' className='outline-none' tabIndex={-1}>
                <Button size='sm' tabIndex={0}>
                  Create account
                </Button>
              </Link>
            )
          ) : (
            <span className='text-secondary text-sm'>{user.email}</span>
          )}
        </div>
      </div>
      <div className='flex items-center justify-between gap-2 pb-1'>
        {groupedAccounts.length === 0 ? (
          <ListRowButton
            size='lg'
            className='border border-border1 rounded-3xl justify-stretch'
            onClick={() => {
              ampli?.walletConnectWalletClicked({
                position: 'portfolio',
              })
              setDialogState(openSelectWalletsDialog())
            }}
          >
            <div className='flex items-center justify-center basis-20'>
              <Image
                src='/assets/backpack.png'
                alt='Backpack'
                width={72}
                height={72}
                className='min-w-20 w-full h-auto'
                priority
              />
            </div>
            <ListRowSide className='grow'>
              <ListRowSideName>Build your portfolio</ListRowSideName>
              <ListRowSideDescription>
                Add your wallets to start trading
              </ListRowSideDescription>
            </ListRowSide>
          </ListRowButton>
        ) : (
          <>
            <WalletStack
              wallets={groupedAccounts}
              onClick={() => updateView({ view: 'wallets' })}
            />
            <button
              type='button'
              className={cn(
                'flex flex-col gap-3 px-3 pt-3 pb-2 rounded-2xl border border-border2 border-dashed !h-[92px] items-start justify-between focus-inner',
                {
                  'w-32': groupedAccounts.length >= 1,
                  'w-full': groupedAccounts.length === 0,
                },
              )}
              onClick={() => {
                ampli?.walletConnectWalletClicked({
                  position: 'portfolio',
                })
                setDialogState(openSelectWalletsDialog())
              }}
            >
              <div className='flex items-center justify-center size-6 bg-surface2-100 rounded-full'>
                <Plus size={16} className='text-primary' />
              </div>
              <span className='text-secondary text-sm font-semibold'>
                Add wallet
              </span>
            </button>
          </>
        )}
      </div>
      <div className='flex flex-col py-2 -mx-4'>
        {user.id !== 'guest' && (
          <ListRowButton
            size='md'
            onClick={() => updateView({ view: 'manage-account' })}
          >
            <ListRowSide className='flex-row items-center gap-4'>
              <div className='flex items-center justify-center size-8'>
                <CircleUser size={24} className='text-tertiary-100' />
              </div>
              <ListRowSideName>Account</ListRowSideName>
            </ListRowSide>
            <ChevronRight size={24} className='text-tertiary-100' />
          </ListRowButton>
        )}
        <ListRowButton
          size='md'
          onClick={() => updateView({ view: 'settings' })}
        >
          <ListRowSide className='flex-row items-center gap-4'>
            <div className='flex items-center justify-center size-8'>
              <Settings size={24} className='text-tertiary-100' />
            </div>
            <ListRowSideName>Settings</ListRowSideName>
          </ListRowSide>
          <ChevronRight size={24} className='text-tertiary-100' />
        </ListRowButton>
        <ListRowButton size='md' onClick={() => showIntercomWidget()}>
          <ListRowSide className='flex-row items-center gap-4'>
            <div className='flex items-center justify-center size-8'>
              <MessageCircleQuestion size={24} className='text-tertiary-100' />
            </div>
            <ListRowSideName>Get support</ListRowSideName>
          </ListRowSide>
          <ChevronRight size={24} className='text-tertiary-100' />
        </ListRowButton>
        <ListRowButton
          size='md'
          onClick={async () => {
            const { showTicket } = await import('@intercom/messenger-js-sdk')

            // Context: https://osmosis-network.slack.com/archives/C08HRJWJLP3/p1755021005380539?thread_ts=1752863643.257479&cid=C08HRJWJLP3
            showTicket('2766457')
          }}
        >
          <ListRowSide className='flex-row items-center gap-4'>
            <div className='flex items-center justify-center size-8'>
              <ThumbsUp size={24} className='text-tertiary-100' />
            </div>
            <ListRowSideName>Leave feedback</ListRowSideName>
          </ListRowSide>
          <ChevronRight size={24} className='text-tertiary-100' />
        </ListRowButton>
        {featureFlags.referrals && user.id !== 'guest' && (
          <ListRowButton
            size='md'
            onClick={() => updateView({ view: 'referrals' })}
          >
            <ListRowSide className='flex-row items-center gap-4'>
              <div className='flex items-center justify-center size-8'>
                <Gift size={24} className='text-tertiary-100' />
              </div>
              <ListRowSideName>Referrals</ListRowSideName>
            </ListRowSide>
            <ChevronRight size={24} className='text-tertiary-100' />
          </ListRowButton>
        )}
      </div>
      <div className='flex flex-col gap-3'>
        {connectedWallets.length >= 1 && user.id === 'guest' && (
          <Button
            type='button'
            size='md'
            variant='danger'
            className='w-full font-semibold'
            onClick={async () => {
              showWalletsDisconnectedToast({
                numberOfWallets: connectedWallets.length,
              })
              disconnectAll()
              const accounts = groupedAccounts.flatMap(
                (account) => account.accounts,
              )
              await removeAccounts.mutateAsync(accounts)

              reset()
            }}
          >
            Remove all wallets
          </Button>
        )}
        {user.id !== 'guest' && (
          <Button
            type='button'
            size='md'
            variant='danger'
            className='w-full'
            onClick={async () => {
              close()
              await logout()
              window.location.reload()
            }}
          >
            Log out
          </Button>
        )}
      </div>
    </motion.div>
  )
}
